import { useState } from 'react';
import {
  BarChart3,
  ChevronDown,
  ChevronRight,
  CreditCard,
  FileClock,
  Gauge,
  LayoutList,
  MessageSquare,
  Package,
  Settings,
  ShieldAlert,
  Truck,
  Users,
  WalletCards,
  type LucideIcon,
} from 'lucide-react';

import type { AdminStore } from '@/client/types/stores';
import { useStoreAnalyticsQuery } from '@/client/api/stores/stores.query';
import { useStoreLowStockQuery, useStoreRefundRequestsQuery, useStoreReturnRequestsQuery } from '@/client/api/stores/parity.query';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * Navigation for the admin Store Manager, mirroring the seller's Store Manager
 * in ff-ui (Dashboard, Messages, Inventory, Fulfilment, Payments, Performance,
 * Team, Settings) plus the admin-only tools. `tab` values are the page's
 * `?tab=` keys, so deep links keep working.
 */
export interface NavLeaf { tab: string; label: string; title: string; icon?: LucideIcon }
export interface NavGroup { label: string; icon: LucideIcon; children: NavLeaf[] }
type NavItem = NavLeaf | NavGroup;

const isGroup = (i: NavItem): i is NavGroup => 'children' in i;

export const STORE_NAV: { section?: string; items: NavItem[] }[] = [
  {
    items: [
      { tab: 'Overview', label: 'Dashboard', title: 'Dashboard', icon: BarChart3 },
      { tab: 'Messages', label: 'Messages', title: 'Messages', icon: MessageSquare },
      { label: 'Inventory', icon: Package, children: [
        { tab: 'Inventory', label: 'Stock', title: 'Inventory' },
        { tab: 'Pricing', label: 'Pricing', title: 'Pricing' },
      ] },
      { label: 'Fulfilment', icon: Truck, children: [
        { tab: 'Orders', label: 'Orders', title: 'Orders' },
        { tab: 'Fulfilment', label: 'Shipping', title: 'Shipping & Fulfilment' },
        { tab: 'Refunds', label: 'Refunds & Returns', title: 'Refunds & Returns' },
      ] },
      { label: 'Payments', icon: CreditCard, children: [
        { tab: 'Transactions', label: 'Transactions', title: 'Transactions' },
        { tab: 'Payout', label: 'Payouts', title: 'Payouts' },
        { tab: 'Vouchers', label: 'Vouchers', title: 'Vouchers' },
        { tab: 'Billing', label: 'Billing & Plans', title: 'Billing & Plans' },
      ] },
      { tab: 'Performance', label: 'Performance', title: 'Performance', icon: Gauge },
      { tab: 'Staff', label: 'Team', title: 'Team', icon: Users },
      { tab: 'Settings', label: 'Settings', title: 'Store Settings', icon: Settings },
    ],
  },
  {
    section: 'Admin',
    items: [
      { tab: 'Listings', label: 'Listings', title: 'Listings', icon: LayoutList },
      { tab: 'Plan', label: 'Plan & commission', title: 'Plan & Commission', icon: WalletCards },
      { tab: 'Moderation', label: 'Moderation', title: 'Moderation', icon: ShieldAlert },
      { tab: 'Audit', label: 'Audit log', title: 'Audit log', icon: FileClock },
    ],
  },
];

const LEAVES: NavLeaf[] = STORE_NAV.flatMap((s) => s.items.flatMap((i) => (isGroup(i) ? i.children : [i])));

/** Heading shown on the content card for a tab. */
export const titleForTab = (tab: string): string => LEAVES.find((l) => l.tab === tab)?.title ?? tab;

const itemClass = (active: boolean) =>
  cn(
    'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
    active ? 'bg-blue-50 text-blue-700' : 'text-gray-800 hover:bg-gray-50',
  );

const Group: React.FC<{ group: NavGroup; tab: string; onSelect: (t: string) => void }> = ({ group, tab, onSelect }) => {
  const containsActive = group.children.some((c) => c.tab === tab);
  const [open, setOpen] = useState(containsActive);
  const expanded = open || containsActive;
  const Icon = group.icon;
  return (
    <div>
      <button type="button" onClick={() => setOpen(!expanded)} className={itemClass(containsActive)} aria-expanded={expanded}>
        <Icon className="h-[18px] w-[18px] flex-shrink-0" />
        <span className="flex-1 text-left">{group.label}</span>
        {expanded ? <ChevronDown className="h-4 w-4 opacity-70" /> : <ChevronRight className="h-4 w-4 opacity-70" />}
      </button>
      {expanded && (
        <div className="mt-1 space-y-0.5 pl-6">
          {group.children.map((c) => (
            <button
              key={c.tab}
              type="button"
              onClick={() => onSelect(c.tab)}
              className={cn(
                'flex w-full items-center rounded-lg px-3 py-2 text-sm transition-colors',
                c.tab === tab ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-700 hover:bg-gray-50',
              )}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const QuickOverview: React.FC<{ store: AdminStore }> = ({ store }) => {
  const { data: totals } = useStoreAnalyticsQuery(store.storeid);
  const { data: lowStock } = useStoreLowStockQuery(store.storeid);
  const { data: refunds } = useStoreRefundRequestsQuery(store.storeid, { status: 'pending', limit: 1 });
  const { data: returns } = useStoreReturnRequestsQuery(store.storeid, { status: 'pending', limit: 1 });
  const pending = (refunds?.total_count ?? 0) + (returns?.total_count ?? 0);
  const rows: [string, React.ReactNode, string?][] = [
    ['Active listings', totals?.active_listings ?? '—'],
    ['Orders', totals?.orders.count.toLocaleString() ?? '—'],
    ['Pending refunds & returns', pending, pending > 0 ? 'text-amber-600' : undefined],
    ['Low stock', lowStock?.total_count ?? '—', (lowStock?.total_count ?? 0) > 0 ? 'text-red-600' : 'text-emerald-600'],
  ];
  return (
    <div className="mt-6 border-t border-gray-100 pt-4">
      <p className="mb-2 px-1 text-sm font-medium text-gray-900">Quick overview</p>
      <dl className="space-y-2 px-1 text-sm">
        {rows.map(([label, value, tone]) => (
          <div key={label} className="flex items-center justify-between gap-2">
            <dt className="text-gray-500">{label}</dt>
            <dd className={cn('font-medium tabular-nums text-gray-900', tone)}>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

/** Sidebar card: grouped navigation + quick overview (large screens). */
export const StoreManagerSidebar: React.FC<{ store: AdminStore; tab: string; onSelect: (t: string) => void }> = ({ store, tab, onSelect }) => (
  <nav className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200/60" aria-label="Store Manager">
    {STORE_NAV.map((section, i) => (
      <div key={section.section ?? i} className={cn(i > 0 && 'mt-5 border-t border-gray-100 pt-4')}>
        {section.section && (
          <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{section.section}</p>
        )}
        <div className="space-y-1">
          {section.items.map((item) =>
            isGroup(item) ? (
              <Group key={item.label} group={item} tab={tab} onSelect={onSelect} />
            ) : (
              <button key={item.tab} type="button" onClick={() => onSelect(item.tab)} className={itemClass(item.tab === tab)}>
                {item.icon && <item.icon className="h-[18px] w-[18px] flex-shrink-0" />}
                {item.label}
              </button>
            ),
          )}
        </div>
      </div>
    ))}
    <QuickOverview store={store} />
  </nav>
);

/** Loading state of the sidebar: nav rows, the Admin block, quick overview. */
export const StoreManagerSidebarSkeleton: React.FC = () => (
  <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200/60" aria-busy="true" aria-label="Loading navigation">
    <div className="space-y-1">
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2.5">
          <Skeleton className="h-[18px] w-[18px] rounded" />
          <Skeleton className={cn('h-4', i % 3 === 0 ? 'w-24' : i % 3 === 1 ? 'w-20' : 'w-28')} />
        </div>
      ))}
    </div>
    <div className="mt-5 space-y-1 border-t border-gray-100 pt-4">
      <Skeleton className="mx-3 mb-2 h-3 w-12" />
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2.5">
          <Skeleton className="h-[18px] w-[18px] rounded" />
          <Skeleton className={cn('h-4', i % 2 ? 'w-20' : 'w-28')} />
        </div>
      ))}
    </div>
    <div className="mt-6 space-y-2 border-t border-gray-100 px-1 pt-4">
      <Skeleton className="mb-3 h-4 w-28" />
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="flex justify-between">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-8" />
        </div>
      ))}
    </div>
  </div>
);

/** Compact section picker for small screens. */
export const StoreManagerMobileNav: React.FC<{ tab: string; onSelect: (t: string) => void }> = ({ tab, onSelect }) => (
  <select
    value={tab}
    onChange={(e) => onSelect(e.target.value)}
    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm font-medium"
    aria-label="Store Manager section"
  >
    {STORE_NAV.map((section, i) => (
      <optgroup key={section.section ?? i} label={section.section ?? 'Store Manager'}>
        {section.items.flatMap((item) =>
          isGroup(item)
            ? item.children.map((c) => <option key={c.tab} value={c.tab}>{`${item.label} · ${c.label}`}</option>)
            : [<option key={item.tab} value={item.tab}>{item.label}</option>],
        )}
      </optgroup>
    ))}
  </select>
);
